import { fetchWithRetry } from './retry.js';
import type {
  IncidentsResponse,
  OutagesResponse,
  PublicStatus,
  StaffTelemetry,
  UptimeResponse,
} from './types.js';

const JSON_HEADERS = { accept: 'application/json' };

/**
 * GET a staff endpoint, retrying transient 5xx.
 *
 * The staff API scales to zero between office visits, so the request burst on
 * a cold page load can draw 503s from cold starts. Retrying absorbs that; a
 * 403 (the WAF's "not staff" verdict) is returned untouched and immediately.
 * Callers keep their own ok/status handling — this only changes the transport.
 */
function getStaff(path: string): Promise<Response> {
  return fetchWithRetry(() => fetch(path, { headers: JSON_HEADERS }));
}

export async function fetchPublicStatus(): Promise<PublicStatus> {
  const res = await fetch('/api/public/status', { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`status fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchUptime(service: string, days = 90): Promise<UptimeResponse> {
  const res = await fetch(
    `/api/public/uptime?service=${encodeURIComponent(service)}&days=${days}`,
    { headers: { accept: 'application/json' } },
  );
  if (!res.ok) throw new Error(`uptime fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchOutages(service: string): Promise<OutagesResponse> {
  const res = await fetch(
    `/api/public/outages?service=${encodeURIComponent(service)}`,
    { headers: { accept: 'application/json' } },
  );
  if (!res.ok) throw new Error(`outages fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch outages for a staff-only service. Mirrors fetchOutages exactly
 * (throws on non-ok) — the outages effect uses Promise.allSettled and
 * discards rejected entries, so throwing degrades gracefully. Unlike
 * fetchStaffStatus/fetchStaffTelemetry, there's no null-as-"not staff"
 * signal here: callers only reach this once isStaff is already known.
 */
export async function fetchStaffOutages(service: string): Promise<OutagesResponse> {
  const res = await getStaff(`/api/staff/outages?service=${encodeURIComponent(service)}`);
  if (!res.ok) throw new Error(`staff outages fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch active + resolved incidents merged, newest-first, capped to 20.
 * Used by the "Incidents" tab. The active-incident banner uses the
 * embedded `active_incidents` array on the /status response so it stays
 * decoupled from this endpoint.
 */
export async function fetchRecentIncidents(): Promise<IncidentsResponse> {
  const res = await fetch('/api/public/incidents?state=all', {
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`incidents fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch active + resolved incidents merged, newest-first, capped to 20, for
 * staff viewers. Mirrors fetchRecentIncidents exactly (throws on non-ok) —
 * callers only reach this once isStaff is already known, so there's no
 * null-as-"not staff" signal here, unlike fetchStaffStatus/fetchStaffTelemetry.
 */
export async function fetchStaffIncidents(): Promise<IncidentsResponse> {
  const res = await getStaff('/api/staff/incidents?state=all');
  if (!res.ok) throw new Error(`staff incidents fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch the staff telemetry payload. Returns null when the WAF blocks
 * the request (non-office IP, 403). Any other non-2xx is also treated
 * as a soft hide rather than a thrown error — the staff panel is
 * progressive enhancement; failing closed silently is the right UX.
 */
export async function fetchStaffTelemetry(): Promise<StaffTelemetry | null> {
  let res: Response;
  try {
    res = await getStaff('/api/staff/telemetry');
  } catch {
    return null;
  }
  if (!res.ok) return null;
  return res.json();
}

/**
 * Fetch the full service list including staff-only services. Returns null
 * when the WAF blocks the request (non-office IP, 403), exactly like
 * fetchStaffTelemetry — the caller falls back to the public list.
 */
export async function fetchStaffStatus(): Promise<PublicStatus | null> {
  let res: Response;
  try {
    res = await getStaff('/api/staff/status');
  } catch {
    return null;
  }
  if (!res.ok) return null;
  return res.json();
}

export async function fetchStaffUptime(service: string, days = 90): Promise<UptimeResponse> {
  const res = await getStaff(
    `/api/staff/uptime?service=${encodeURIComponent(service)}&days=${days}`,
  );
  if (!res.ok) throw new Error(`staff uptime fetch failed: ${res.status}`);
  return res.json();
}
